import {Scope} from '@wix/app-settings-client';
import {IWidgetControllerConfig} from '@wix/native-components-infra';
import {I$WWrapper, IFedOpsLogger} from '@wix/native-components-infra/dist/es/src/types/types';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {APP_DEFINITION_ID, StoresWidgetID} from '@wix/wixstores-client-core/dist/es/src/constants';
import {withErrorReporting} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/errorReporter';
import {CreateControllerFn} from '@wix/yoshi-flow-editor';
import _ from 'lodash';
import {PRODUCT_PAGE_WIXCODE_APP_NAME, productPageFedopsEvent, UserInputType} from '../constants';
import {wixCodeGetProduct} from '../services/getProduct';
import {IProductPageControllerConfig} from '../types/app-types';
import {
  OnChoiceSelectedCallbackType,
  OnQuantityChangedCallbackType,
  OverrideCallback,
  ProductPageStore,
} from './ProductPageStore';
import {SPECS} from '../specs';

type ResolveFn<T> = (value: T | PromiseLike<T>) => void;
export type ReportError = (e: string | Error, shouldRethrowException?: boolean) => any;

export const ProductPageController: CreateControllerFn = (controllerParams) => {
  const {controllerConfig, flowAPI} = controllerParams;
  const {setProps} = controllerConfig;
  const siteStore = controllerParams.appData.context.siteStore;
  let onReadyResolver: ResolveFn<void>;

  let productPageStore: ProductPageStore;
  let config: IProductPageControllerConfig = _.clone(controllerConfig.config);

  const fedopsLoggerFactory = siteStore.platformServices.fedOpsLoggerFactory;
  const fedopsLogger: IFedOpsLogger = fedopsLoggerFactory.getLoggerForWidget({
    appId: APP_DEFINITION_ID,
    widgetId: StoresWidgetID.PRODUCT_PAGE,
    appName: PRODUCT_PAGE_WIXCODE_APP_NAME,
  });

  const waitUntilReady = new Promise<void>((resolve) => {
    onReadyResolver = resolve;
  });

  const controllerConfigRef: IWidgetControllerConfig = controllerConfig;

  const exports = () =>
    withErrorReporting(flowAPI.reportError)({
      getProduct: async () => {
        await waitUntilReady;
        const fedopsInteraction = productPageFedopsEvent.WixCodeGetProduct;
        fedopsLogger.interactionStarted(fedopsInteraction);
        flowAPI.panoramaClient.transaction(fedopsInteraction).start();
        const onSuccess = () => {
          fedopsLogger.interactionEnded(fedopsInteraction);
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
        };
        return wixCodeGetProduct(siteStore, flowAPI.reportError, onSuccess);
      },
      // @deprecated - New method is getCustomText
      getCustomTextFieldsValues: (): string[] => {
        const fedopsInteraction = productPageFedopsEvent.WixCodeGetCustomTextFieldValues;
        fedopsLogger.interactionStarted(fedopsInteraction);
        flowAPI.panoramaClient.transaction(fedopsInteraction).start();
        const customTextFieldsValues = productPageStore.userInputs.text;
        flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
        fedopsLogger.interactionEnded(fedopsInteraction);
        return customTextFieldsValues;
      },
      ...(flowAPI.experiments.enabled(SPECS.PRODUCT_PAGE_NEW_WIX_CODE_API) && {
        // eslint-disable-next-line sonarjs/no-identical-functions
        getCustomText: async (): Promise<string[]> => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeGetCustomText;
          fedopsLogger.interactionStarted(fedopsInteraction);
          flowAPI.panoramaClient.transaction(fedopsInteraction).start();
          const customTextFieldsValues = productPageStore.customText;
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
          return customTextFieldsValues;
        },
        getQuantity: async () => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeGetQuantity;
          fedopsLogger.interactionStarted(fedopsInteraction);
          const selectedQuantity = productPageStore.selectedQuantity;
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
          return selectedQuantity;
        },
        setQuantity: async (value: number) => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeSetQuantity;
          fedopsLogger.interactionStarted(fedopsInteraction);
          productPageStore.handleUserInput(UserInputType.Quantity, value, 0);
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
        },
        onQuantityChanged: async (callback: OnQuantityChangedCallbackType) => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnQuantityChanged;
          fedopsLogger.interactionStarted(fedopsInteraction);
          productPageStore.setOnQuantityChangedCallback(callback);
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
        },
        getSelectedVariantId: async () => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeGetSelectedVariantId;
          fedopsLogger.interactionStarted(fedopsInteraction);
          const selectedVariantId = productPageStore.selectedVariant?.id;
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
          return selectedVariantId;
        },
        getSelectedChoices: async () => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeGetSelectedChoices;
          fedopsLogger.interactionStarted(fedopsInteraction);
          const selectedChoices = productPageStore.selectedChoices;
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
          return selectedChoices;
        },
        onChoiceSelected: async (callback: OnChoiceSelectedCallbackType) => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnChoiceSelected;
          fedopsLogger.interactionStarted(fedopsInteraction);
          productPageStore.setOnChoiceSelectedCallback(callback);
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
        },
        onAddToCart: async (callback: OverrideCallback) => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnAddToCart;
          fedopsLogger.interactionStarted(fedopsInteraction);
          productPageStore.setOnAddToCartCallback(callback);
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
        },
        setAddToCartLabel: async (text: string) => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnAddToCart;
          fedopsLogger.interactionStarted(fedopsInteraction);
          productPageStore.setAddToCartTextOverride(text);
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
        },
        onBuyNow: async (callback: OverrideCallback) => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnBuyNow;
          fedopsLogger.interactionStarted(fedopsInteraction);
          productPageStore.setOnBuyNowCallback(callback);
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
        },
        setBuyNowLabel: async (text: string) => {
          await waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnBuyNow;
          fedopsLogger.interactionStarted(fedopsInteraction);
          productPageStore.setBuyNowTextOverride(text);
          flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          fedopsLogger.interactionEnded(fedopsInteraction);
        },
      }),
    });

  const updateConfig = (_$w: I$WWrapper, newConfig: IProductPageControllerConfig) => {
    const {publicData} = newConfig;
    config = _.clone(newConfig);
    return productPageStore.updateState(config, publicData);
  };

  const updateAppSettings = (_$w: I$WWrapper, updates: {[key: string]: any}) => {
    /* istanbul ignore else */
    if (updates.scope === Scope.COMPONENT && updates.source === 'app-settings') {
      productPageStore.updateAppSettings(updates.payload);
    }
  };

  const load = async () => {
    const {publicData} = config;
    if (productPageStore) {
      return;
    }

    productPageStore = new ProductPageStore(
      config,
      controllerParams.dangerousPublicDataOverride,
      publicData,
      setProps,
      siteStore,
      controllerConfigRef.config.externalId || '',
      flowAPI.reportError,
      controllerConfigRef.compId,
      createSlotVeloAPIFactory(controllerConfigRef),
      controllerConfigRef,
      flowAPI.errorHandler,
      flowAPI
    );

    await productPageStore
      .setInitialState()
      .then(() => {
        onReadyResolver();
      })
      .catch(flowAPI.reportError);
  };

  const init = async () => {
    await siteStore.loadClientConfig();
    controllerParams.controllerConfig.wixCodeApi.location.onChange(() => void load());
    await load();
  };

  return {
    async pageReady() {
      await init();
    },
    load,
    exports,
    updateConfig,
    updateAppSettings,
  };
};
